/**
 * The message-key set the view-model emits for the Phase 1 slice (§9.1).
 *
 * INV-STABLE-ID lives here at the presentation boundary: the view-model carries
 * these `MessageId`s (never final strings); Agent 6's `I18nProvider.t` resolves
 * them. This module is the contract Agent 5 and Agent 6 agree on for the slice —
 * every key the builder can emit appears in `VIEW_MESSAGE_KEYS`, so the catalog
 * is checkable for completeness.
 *
 * Note: claim labels, constraint labels and System-Claim descriptions are NOT
 * listed here — those keys are carried on the domain objects themselves
 * (`Claim.labelId`, `SystemClaim.descriptionId`, …) and are owned by whoever
 * declares the entity. This file holds only the *structural* keys the builder
 * introduces.
 */
import { messageId } from '@progression/contracts';
/**
 * Structural message keys, grouped by purpose. Values are branded `MessageId`s.
 * Params each key expects (for the catalog author) are noted in comments.
 */
export const MSG = {
    // --- layout / sections ---
    inquiryTitle: messageId('view.inquiry.title'),
    sectionClaims: messageId('view.section.claims'),
    sectionIntegrity: messageId('view.section.integrity'),
    // --- the three axes (full register; params shown) ---
    axisWarrantLabel: messageId('axis.warrant.label'),
    axisWarrantValue: messageId('axis.warrant.value'), // params: { w: number }
    axisEmpiricalLabel: messageId('axis.empirical.label'),
    axisEmpiricalValue: messageId('axis.empirical.value'), // params: { kind: string }
    axisModalLabel: messageId('axis.modal.label'),
    axisModalValue: messageId('axis.modal.value'), // params: { m: number }
    // --- a single coarse axis line for the kid register ---
    axisKidSummary: messageId('axis.kid.summary'), // params: { w: number }
    // --- presentational verdict states ---
    stateExplorable: messageId('state.explorable'),
    stateRefuted: messageId('state.refuted'),
    stateOutside: messageId('state.outside'),
    // --- verdict detail ---
    verdictConflict: messageId('verdict.conflict'), // params: { constraints: string }
    verdictOutside: messageId('verdict.outside'), // params: { missing: string }
    // --- precision-loss notes (A3 / A7) ---
    lossRegisterPlain: messageId('loss.register.plain'),
    lossRegisterKid: messageId('loss.register.kid'),
    lossProfileDetail: messageId('loss.profile-detail'),
    lossConflictDetail: messageId('loss.conflict-detail'),
    // --- integrity lanes (Machine track values) ---
    laneVerified: messageId('lane.verified'),
    laneUnverified: messageId('lane.unverified'),
    laneConflict: messageId('lane.conflict'),
    // ===========================================================================
    // Phase 2 additions (Agent-5-owned "view chrome" keys). These extend the slice
    // vocabulary beyond Agent 6's Phase 1 catalog; they are listed for Agent 6 to
    // fold into the catalog (see DECISIONS_Agent5_Phase2.md P2-C1). Entity labels
    // (claim/constraint/edge/sysclaim) are NOT here — they travel on the objects.
    // ===========================================================================
    // --- additional sections ---
    sectionConstraints: messageId('view.section.constraints'),
    sectionEdges: messageId('view.section.edges'),
    sectionFrame: messageId('view.section.frame'),
    sectionExposure: messageId('view.section.exposure'),
    // --- constraint statuses (the no-ambiguity calculus) ---
    statusAdoptedFormal: messageId('status.adopted-formal'),
    statusAdoptedInterpretive: messageId('status.adopted-interpretive'),
    statusSuspended: messageId('status.suspended'),
    // --- typed-edge effects (each edge kind's effect on the config) ---
    edgeEffectConstraint: messageId('edge.effect.constraint'), // composes / can refute
    edgeEffectResource: messageId('edge.effect.resource'), // inert
    edgeEffectHeuristic: messageId('edge.effect.heuristic'), // inert
    edgeEffectShared: messageId('edge.effect.shared-foundation'), // inert
    // --- framing (a frame is a config; reframe = change the active set) ---
    frameActive: messageId('view.frame.active'), // params: { count: number }
    // --- per-claim exposure (adopted-interpretive dependencies) ---
    exposureNone: messageId('view.exposure.none'),
    exposureDep: messageId('view.exposure.dep'), // params: { id: string }
    // --- the three integrity-lane TRACK labels (B: side-by-side, own vocabulary) ---
    laneTrackMachine: messageId('lane.machine'),
    laneTrackExpert: messageId('lane.expert'),
    laneTrackCommunity: messageId('lane.community'),
    // --- Expert lane values ---
    expertAudited: messageId('lane.expert.audited'),
    expertInProgress: messageId('lane.expert.in-progress'),
    expertNotReviewed: messageId('lane.expert.not-reviewed'),
    // --- Community lane values ---
    communityFlags: messageId('lane.community.flags'),
    communityNone: messageId('lane.community.none'),
    // --- interactive control labels (rendered by the ViewController) ---
    controlChallenge: messageId('control.challenge'),
    controlActivate: messageId('control.activate'),
    controlMakeInterpretive: messageId('control.make-interpretive'),
    controlImportEdge: messageId('control.import-edge'),
    controlPromote: messageId('control.promote'),
    controlUndo: messageId('control.undo'),
    controlRedo: messageId('control.redo'),
    controlRegister: messageId('control.register'),
    // --- promotion outcome chrome ---
    promoteRejected: messageId('view.promote.rejected'), // header before the reason id
    promoteAccepted: messageId('view.promote.accepted'),
    // --- the keystone-guard reason ids (mirrored from the kernel; resolve same) ---
    promoteReasonRefutationTerminal: messageId('promote.proof-cannot-cure-refutation'),
    promoteReasonKeystone: messageId('promote.keystone-needs-empirical'),
    promoteReasonEmpiricalNotFixed: messageId('promote.empirical-not-fixed'),
    promoteReasonModalNotFixed: messageId('promote.modal-not-fixed'),
    promoteReasonWarrantNotImproved: messageId('promote.warrant-not-improved'),
    // ===========================================================================
    // Compute Integration additions (plan §2.4 — exactly these two new keys).
    // Agent 6 owns the catalog text + result formatting; until they land, the
    // provider renders «id» gracefully (INV-STABLE-ID safe).
    // ===========================================================================
    /**
     * One computation's evidence line, shown inline beside (never as) the verdict.
     * Params (the contract for Agent 6): { level, engine, op, result, configDigest }.
     *   - `level` ∈ {full, plain, kid} drives an ICU select for register verbosity
     *     (full = engine+op+result+digest; plain = coarser; kid = "checked by computer");
     *   - `result` is pre-serialized to a string here (Str.params forbids arrays).
     */
    evidenceComputation: messageId('evidence.computation'),
    /** Numerical-truncation note. Params: { decimals }. */
    lossNumericRounding: messageId('loss.numeric-rounding'),
};
/** Every structural key the builder may emit — handed to Agent 6 for the catalog. */
export const VIEW_MESSAGE_KEYS = Object.freeze(Object.values(MSG));
//# sourceMappingURL=messages.js.map